import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Search, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { empName } from "./api";
import { PageHeader, SubTabs, Panel, Empty, Loading, Avatar } from "./ui";
import { useOrgRefs, EmployeeSheet } from "./employeeShared";
import Phase2Page from "./Phase2Page";

const TABS = [
  { key: "peers", label: "Peers", to: "/crm-app/team/peers" },
  { key: "approvals", label: "Approvals", to: "/crm-app/team/approvals", phase2: true },
];

const Card = ({ e, dep, onOpen }) => (
  <button onClick={() => onOpen(e.employee_id)} className="text-left bg-white rounded-xl border border-slate-200/80 p-4 hover:border-slate-300 hover:shadow-sm transition-[border-color,box-shadow] flex gap-3" data-testid={`peer-card-${e.employee_id}`}>
    <Avatar name={empName(e)} size="h-11 w-11" />
    <div className="min-w-0">
      <p className="font-medium text-slate-900 truncate">{empName(e)}</p>
      <p className="text-xs text-slate-500 truncate">{e.designation || "—"}</p>
      <p className="text-[11px] text-slate-400 mt-1 truncate">{e.employee_id}{dep ? ` · ${dep}` : ""}</p>
    </div>
  </button>
);

export default function TeamPage() {
  const { tab = "peers" } = useParams();
  const [params, setParams] = useSearchParams();
  const refs = useOrgRefs();
  const [q, setQ] = useState("");
  const [group, setGroup] = useState(false);
  const [viewing, setViewing] = useState(params.get("emp"));
  useEffect(() => { if (params.get("emp")) { setViewing(params.get("emp")); setParams({}, { replace: true }); } }, [params, setParams]);
  if (tab === "approvals") return <div><PageHeader title="Team" /><SubTabs tabs={TABS} /><Phase2Page module="approvals" /></div>;
  if (!refs.loaded) return <Loading />;
  const depName = (id) => refs.departments.find((d) => d.department_id === id)?.name;
  const list = refs.employees.filter((e) => [empName(e), e.employee_id, e.designation, e.email, depName(e.department_id)].join(" ").toLowerCase().includes(q.toLowerCase()));
  const groups = group
    ? [...refs.departments.map((d) => [d.name, list.filter((e) => e.department_id === d.department_id)]), ["No department", list.filter((e) => !depName(e.department_id))]].filter(([, l]) => l.length)
    : [[null, list]];
  return (
    <div data-testid="team-page">
      <PageHeader title="Team" subtitle={`${refs.employees.length} people in your organisation`} />
      <SubTabs tabs={TABS} />
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative w-full sm:w-80"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input className="pl-9 bg-white" placeholder="Search people" value={q} onChange={(e) => setQ(e.target.value)} data-testid="team-search-input" /></div>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={group} onChange={(e) => setGroup(e.target.checked)} data-testid="team-group-toggle" />Group by department</label>
      </div>
      {list.length === 0 ? <Panel><Empty icon={Users} title={refs.employees.length ? "No one matches your search" : "No team members yet"} hint="Employee records are managed by your Business Owner or Administrator." /></Panel> : groups.map(([name, l]) => (
        <div key={name || "all"} className="mb-6" data-testid={name ? `team-group-${name}` : undefined}>
          {name && <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{name} · {l.length}</p>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">{l.map((e) => <Card key={e.employee_id} e={e} dep={depName(e.department_id)} onOpen={setViewing} />)}</div>
        </div>
      ))}
      <EmployeeSheet employeeId={viewing} onClose={() => setViewing(null)} refs={refs} />
    </div>
  );
}
